import { useRef, useState, type PointerEvent, type CSSProperties } from "react";

type Choice = "A" | "B";
type Route = { title: string; detail: string };

/** The artwork stays intact: gestures steer the wake, not the background boat. */
export function RouteNavigation({ choices, selected, onChoose }: {
  choices: Record<Choice, Route>; selected: Choice | null; onChoose: (choice: Choice) => void;
}) {
  const [preview, setPreview] = useState<Choice | null>(null);
  const [dragging, setDragging] = useState(false);
  const [strength, setStrength] = useState(0);
  const gesture = useRef<{ id: number; x: number; y: number; threshold: number } | null>(null);
  const committed = useRef(false);
  const active = selected ?? preview;

  function choose(choice: Choice) {
    if (selected || committed.current) return;
    committed.current = true;
    onChoose(choice);
  }
  function direction(event: PointerEvent): Choice | null {
    const start = gesture.current;
    if (!start) return null;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    return Math.abs(dx) >= start.threshold && Math.abs(dx) > Math.abs(dy) * 1.5 ? (dx < 0 ? "A" : "B") : null;
  }
  function cancel() {
    gesture.current = null;
    setDragging(false);
    setPreview(null);
    setStrength(0);
  }
  function down(event: PointerEvent<HTMLDivElement>) {
    if (!event.isPrimary || event.button !== 0 || selected || committed.current) return;
    gesture.current = { id: event.pointerId, x: event.clientX, y: event.clientY,
      threshold: Math.max(44, Math.min(90, event.currentTarget.clientWidth * .14)) };
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
  }
  function move(event: PointerEvent<HTMLDivElement>) {
    if (gesture.current?.id !== event.pointerId) return;
    // A vertical gesture cancels the attempt, even if the finger later veers sideways.
    const dx = event.clientX - gesture.current.x;
    const dy = event.clientY - gesture.current.y;
    if (Math.abs(dy) > 24 && Math.abs(dy) > Math.abs(dx)) { cancel(); return; }
    const amount = Math.min(1, Math.abs(dx) / gesture.current.threshold);
    setStrength(amount);
    setPreview(Math.abs(dx) > 4 ? (dx < 0 ? "A" : "B") : null);
  }
  function up(event: PointerEvent<HTMLDivElement>) {
    if (gesture.current?.id !== event.pointerId) return;
    const choice = direction(event);
    cancel();
    if (choice) choose(choice);
  }

  return <div className={"v2-route-navigation" + (dragging ? " is-dragging" : "") + (selected ? " is-confirmed" : "")} data-active={active ?? ""}
    style={{ "--route-strength": dragging ? strength : 1 } as CSSProperties}>
    <div className="v2-route-water" aria-hidden="true" onPointerDown={down} onPointerMove={move}
      onPointerUp={up} onPointerCancel={cancel} onLostPointerCapture={cancel}>
      <svg className="v2-water-paths" viewBox="0 0 1000 300" preserveAspectRatio="none">
        {(["A", "B"] as const).map(choice => <g key={choice} className={active === choice ? "is-active" : ""}>
          <path className="v2-water-path" pathLength="1" d={choice === "A" ? "M500 18 C450 100 260 95 155 270" : "M500 18 C550 100 740 95 845 270"} />
          <path className="v2-water-arrow" d={choice === "A" ? "M153 253 L155 270 L172 263" : "M828 263 L845 270 L847 253"} />
        </g>)}
        <ellipse className="v2-water-origin" cx="500" cy="18" rx="12" ry="5" />
      </svg>
    </div>
    <div className="v2-routes">
      {(["A", "B"] as const).map(choice => <button key={choice} type="button"
        className={"v2-route-text is-" + (choice === "A" ? "left" : "right") + (selected === choice ? " is-selected" : "")}
        aria-pressed={selected === choice} disabled={Boolean(selected)} onClick={() => choose(choice)}
        onMouseEnter={() => { if (!dragging) setPreview(choice); }} onMouseLeave={() => { if (!dragging) setPreview(null); }}
        onFocus={() => setPreview(choice)} onBlur={() => setPreview(null)}>
        <strong>{choices[choice].title}</strong><small>{choices[choice].detail}</small>
      </button>)}
      <p className="v2-route-instruction" role="status">{selected ? "航向已确定，正在继续前行…" : preview && dragging ? strength >= 1 ? `松手，${choices[preview].title}` : "再向这个方向拖动一点…" : "← 左右拖动或滑动水面，也可点击航向 →"}</p>
    </div>
  </div>;
}
